import { useAuth } from "@clerk/clerk-react";
import i18next from "i18next";
import { useEffect } from "react";
import { setAuthErrorHandler, setLanguageGetter, setTokenGetter } from "@/api/client";
import { env } from "@/shared/config/env";

/**
 * Bridges Clerk into the API client. Renders nothing. Mounted inside
 * ClerkProvider so `useAuth` is available; the client stays framework-agnostic
 * and just calls the injected getter per request.
 *
 * Also recovers from a rejected token (API 401): force-refresh the session token
 * so the next request succeeds, or — if the session is actually gone — sign out,
 * which sends the user to sign-in via the route guard.
 */
export function AuthBridge() {
  const { getToken, signOut } = useAuth();

  useEffect(() => {
    setTokenGetter((opts) => getToken(opts));
    return () => setTokenGetter(async () => null);
  }, [getToken]);

  useEffect(() => {
    setLanguageGetter(() => i18next.resolvedLanguage ?? i18next.language ?? "en");
    return () => setLanguageGetter(() => "en");
  }, []);

  useEffect(() => {
    // While bypassing auth locally, never auto-sign-out on a 401 — there's no
    // Clerk session to refresh, and the backend bypass should keep requests 200.
    if (env.AUTH_BYPASS) return;
    let recovering = false;
    setAuthErrorHandler(() => {
      if (recovering) return; // de-dupe a burst of concurrent 401s
      recovering = true;
      void (async () => {
        try {
          const token = await getToken({ skipCache: true }).catch(() => null);
          if (!token) await signOut({ redirectUrl: "/" });
        } finally {
          setTimeout(() => {
            recovering = false;
          }, 3000);
        }
      })();
    });
    return () => setAuthErrorHandler(() => {});
  }, [getToken, signOut]);

  return null;
}
